<template>
  <main id="home" class="site-main" :style="cssVars">
    <div class="grid-x grid-padding-x">
      <div class="cell">
        <div class="grid-x grid-padding-x">
          <div class="welcome cell large-auto">
            <div class="welcome__title">{{ welcomeMessage }},
              {{ shop ? shop.name : '' }}
            </div>

            <div class="welcome__content">
              <div v-if="shop.scope_update_required">
                <a class="button button--small button--scope-alert css-tooltip" style="margin-bottom:5px;" :href="shop.scope_update_auth_url" target="_blank"><i class="fas fa-exclamation-triangle"></i> SCOPE UPDATE REQUIRED<span class="css-tooltip__text">For full app functionality, the app's permissions need a quick update. Click here to approve them — it only takes a moment.</span></a>
              </div>
            </div>
          </div>

          <div class="stats cell large-shrink">
            <div>
              <div class="grid-x">
                <div class="cell auto label" v-if="shop.myshopify_domain == 'tiny-brambles.myshopify.com'">
                  <div class="label__heading">Profit</div>
                  <div class="label__content">{{ storeProfit }}</div>
                </div>

                <div class="cell auto label warning">
                  <div class="label__heading">Due</div>
                  <div class="label__content">{{ commissionDue }}</div>
                </div>

                <div class="cell auto label success">
                  <div class="label__heading">Paid</div>
                  <div class="label__content">{{ commissionPaid }}</div>
                </div>

                <div class="cell auto label">
                  <div class="label__heading">Earned</div>
                  <div class="label__content">{{ commissionEarned }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="grid-x grid-margin-x">
          <div class="cell small-12 small-order-1">
            <UpgradeBanner
              v-if="shop"
              :shop="shop"
              @open-offer="showOfferModal = true"
              @watch-video="key => { watchingVideo = upgradeVideos[key] }"
              @activate="enableNewApp" />
          </div>

          <div class="panel cell medium-6 xlarge-4 small-order-2">
            <div class="panel__header">
              <h3 class="panel__title">Commissions Due</h3>
            </div>

            <div class="panel__content">
              <commissionsDue></commissionsDue>
            </div>
          </div>

          <div class="panel cell medium-6 xlarge-3 small-order-3">
            <div class="panel__header">
              <h3 class="panel__title">Recent Payouts</h3>
            </div>

            <recentPayouts></recentPayouts>
          </div>

          <div class="panel cell xlarge-5 small-order-4">
            <topEarners></topEarners>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal for the new-app upgrade offer -->
    <UpgradeOfferModal
      :show="showOfferModal"
      :shop="shop"
      @close="showOfferModal = false"
      @watch-video="key => { watchingVideo = upgradeVideos[key] }" />

    <!-- The new app's migration videos, opened from the banner or over the offer modal. -->
    <UpgradeVideoModal :video="watchingVideo" @close="watchingVideo = null" />

    <!-- Modal for App Activation Confirmation -->
    <ActivationConfirmModal
      :show="showActivationModal"
      @close="showActivationModal = false"
      @confirm="confirmActivation" />
  </main>

</template>

<script>
import client from '@/mixins/client'
import utils from '@/mixins/utils'
import recentPayouts from '@/pages/vendors/sections/recentPayouts'
import commissionsDue from '@/pages/vendors/sections/commissionsDue'
import topEarners from '@/pages/vendors/sections/topEarners'
import UpgradeBanner from './components/UpgradeBanner'
import UpgradeOfferModal from './components/UpgradeOfferModal'
import UpgradeVideoModal from './components/UpgradeVideoModal'
import { upgradeVideos } from './components/upgradeVideos'
import ActivationConfirmModal from './components/ActivationConfirmModal'

export default {
  name: 'home',
  mixins: [client, utils],
  components: {
    recentPayouts,
    commissionsDue,
    topEarners,
    UpgradeBanner,
    UpgradeOfferModal,
    UpgradeVideoModal,
    ActivationConfirmModal
  },
  data: function () {
    return {
      welcomeMessage: 'Good Morning',
      commissionDue: 0,
      commissionPaid: 0,
      commissionEarned: 0,
      storeProfit: 0,
      showOfferModal: false,
      showActivationModal: false,
      upgradeVideos,
      // The upgradeVideos entry playing in UpgradeVideoModal, or null.
      watchingVideo: null
    }
  },
  methods: {
    enableNewApp () {
      this.showActivationModal = true
    },
    confirmActivation () {
      const shopDomain = this.shop && this.shop.myshopify_domain ? this.shop.myshopify_domain : ''

      this.request('patch', '/shopify/shops/request-activation/', {
        shop: shopDomain
      }).then(response => {
        this.$store.state.shop.activation_requested = true
        this.$store.state.shop.upgrade_process_step = 6
        this.showActivationModal = false
      }).catch(error => {
        console.error('Activation request failed:', error)
      })
    },
    getTotals: function () {
      this.request('get', '/vendors/commission-totals/combined/').then(
        (response) => {
          this.commissionDue = this.formatMoney(response.commission_due)
          this.commissionPaid = this.formatMoney(response.commission_paid)
          this.commissionEarned = this.formatMoney(response.commission_earned)
          this.storeProfit = this.formatMoney(response.store_profit)
        }
      )
    },
    getPortalColor (key) {
      if (this.$store.state.metas && this.$store.state.metas[key] && this.$store.state.metas[key].meta_value !== '') {
        return this.$store.state.metas[key].meta_value
      }
      return false
    }
  },
  computed: {
    shop () {
      return this.$store.state.shop
    },
    meta () {
      return this.$store.state.metas
    },
    primaryColor () {
      let color = '#eb008c'
      if (this.getPortalColor && this.getPortalColor('vendor_portal_primary_color') !== false) {
        color = this.getPortalColor('vendor_portal_primary_color')
      }
      return color
    },
    secondaryColor () {
      let color = '#7101a5'
      if (this.getPortalColor && this.getPortalColor('vendor_portal_secondary_color') !== false) {
        color = this.getPortalColor('vendor_portal_secondary_color')
      }
      return color
    },
    cssVars () {
      return {
        '--primary-color': this.primaryColor,
        '--secondary-color': this.secondaryColor
      }
    }
  },
  created () {
    this.commissionDue = this.formatMoney(0)
    this.commissionPaid = this.formatMoney(0)
    this.commissionEarned = this.formatMoney(0)

    this.getTotals()

    let date = new Date()
    let hours = date.getHours()
    this.welcomeMessage = hours < 12 ? 'Good Morning' : hours < 18 ? 'Good Afternoon' : 'Good Evening'
  }
}
</script>

<style lang="scss">
@import "../../styles/mixins";
@import "../../styles/variables";

.welcome {
  &__title {
    font-size: 24px;
    font-weight: 900;
  }

  &__content {
    /*max-width: 600px;*/
  }
}

.stats {
  padding-top: 10px;

  .grid-x {
    margin: 0 -2px;
  }

  .cell {
    flex: none;
  }

  .label {
    margin: 0 2px 15px;
    padding-top: 15px;
    padding-bottom: 15px;
    text-align: left;

    &__heading {
      font-size: 16px;
      margin-bottom: 5px;
    }

    &__content {
      font-size: 18px;
      font-weight: 700;
      min-width: 180px;
    }
  }
}

@media (min-width: 641px) {
  .stats {
    padding-top: 0;

    .label {

      &__heading {
        font-size: 18px;
        margin-bottom: 5px;
      }

      &__content {
        font-size: 24px;
        font-weight: 700;
      }
    }
  }
}

// Pulsing glow to draw attention to the conditional "scope update required" CTA.
// Uses an amber alert tone so it reads as a warning against the pink primary button.
@keyframes scopeAlertGlow {
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(193, 64, 157, 0);
  }
  50% {
    box-shadow: 0 0 5px 3px rgba(241, 100, 201, 0.55);
  }
}

@keyframes scopeAlertIconPulse {
  // Quick fade out and back in near the start, then hold at full opacity for the rest.
  0% {
    opacity: 1;
  }
  15% {
    opacity: 0.5;
  }
  30% {
    opacity: 1;
  }
  100% {
    opacity: 1;
  }
}

.button.button--scope-alert {
  background-color: #eb008c;
  animation: scopeAlertGlow 2s ease-in-out infinite;
  cursor: pointer; // it's a clickable CTA, override .css-tooltip's "help" cursor

  // The Font Awesome icon renders as an inline SVG; briefly fade it out/in, then hold.
  .svg-inline--fa {
    animation: scopeAlertIconPulse 2.5s ease-in-out infinite;
  }

  // Keep the hover tooltip readable. The base .button forces uppercase text, and
  // the shared .css-tooltip centers a 20rem box over the button — which overflows
  // off-screen on this left-aligned CTA. Anchor it to the left edge instead.
  // Qualify with &.css-tooltip (the CTA carries both classes) so this is specificity
  // (0,3,0) and beats the base .css-tooltip .css-tooltip__text (0,2,0) regardless of
  // source order — production's cssnano reorders equal-specificity rules and would
  // otherwise let the base centering win (works in dev, breaks in the prod build).
  &.css-tooltip .css-tooltip__text {
    text-transform: none;
    white-space: normal; // .button sets nowrap, which the tooltip inherits and overflows the box
    left: 0;
    transform: none;
  }

  // Respect users who prefer reduced motion: keep a static emphasis instead.
  @media (prefers-reduced-motion: reduce) {
    animation: none;
    box-shadow: 0 0 5px 2px rgba(255, 179, 0, 0.55);

    .svg-inline--fa {
      animation: none;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/apps/vendorconsignment/home.vue